import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';

// Types
interface Building {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  floorCount?: number;
  roomsCount?: number;
  holidaysCount?: number;
  _count?: {
    floors: number;
  };
  createdAt: string;
  updatedAt: string;
  floors?: Floor[];
}

interface Floor {
  id: string;
  number: number;
  name: string;
  buildingId: string;
  isActive: boolean;
  roomCount?: number;
  createdAt: string;
  updatedAt: string;
  rooms?: Room[];
}

interface Room {
  id: string;
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description?: string;
  specialtyTags: string[];
  allowedSpecialties: string[];
  defaultBuffer: number;
  status: 'ACTIVE' | 'OUT_OF_SERVICE';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateBuildingData {
  name: string;
  isActive?: boolean;
}

interface UpdateBuildingData {
  name?: string;
  isActive?: boolean;
}

interface CreateFloorData {
  name: string;
  buildingId: string;
  isActive?: boolean;
}

interface UpdateFloorData {
  name?: string;
  isActive?: boolean;
}

interface CreateRoomData {
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description?: string;
  specialtyTags?: string[];
  allowedSpecialties?: string[];
  defaultBuffer?: number;
  status?: 'ACTIVE' | 'OUT_OF_SERVICE';
  sessionTemplateIds?: string[]; // NEW: Template assignment
}

interface UpdateRoomData {
  number?: string;
  name?: string;
  capacity?: number;
  description?: string;
  specialtyTags?: string[];
  allowedSpecialties?: string[];
  defaultBuffer?: number;
  status?: 'ACTIVE' | 'OUT_OF_SERVICE';
}

// Query keys
const queryKeys = {
  buildings: ['buildings'] as const,
  building: (id: string) => ['buildings', id] as const,
  allFloors: ['floors'] as const,
  floors: (buildingId: string) => ['floors', buildingId] as const,
  floor: (id: string) => ['floors', id] as const,
  allRooms: ['rooms'] as const,
  rooms: (floorId: string) => ['rooms', floorId] as const,
  room: (id: string) => ['rooms', id] as const,
};

// Building hooks
export const useBuildings = () => {
  return useQuery({
    queryKey: queryKeys.buildings,
    queryFn: async () => {
      // api.get() returns JSON directly: { success: true, data: { buildings: [...], pagination: {...} } }
      const response = await api.get('/buildings');
      if (response.success && response.data && response.data.buildings) {
        // Transform API response to match frontend expectations
        return response.data.buildings.map((building: any) => ({
          ...building,
          floorCount: building._count?.floors || 0,  // Map _count.floors to floorCount
          roomCount: building.roomsCount || 0         // Map roomsCount to roomCount for buildings
        })) as Building[];
      }
      throw new Error(response.error?.message || 'Failed to fetch buildings');
    },
    retry: 3,
    retryDelay: 1000,
  });
};

export const useBuilding = (id: string) => {
  return useQuery({
    queryKey: queryKeys.building(id),
    queryFn: async () => {
      // api.get() returns JSON directly: { success: true, data: {...} }
      const response = await api.get(`/buildings/${id}`);
      if (response.success && response.data) {
        return response.data as Building;
      }
      throw new Error(response.error?.message || 'Failed to fetch building');
    },
    enabled: !!id,
    retry: 3,
    retryDelay: 1000,
  });
};

export const useCreateBuilding = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateBuildingData) => {
      const response = await api.post('/buildings', data);
      return response.data as Building;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
    },
  });
};

export const useUpdateBuilding = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBuildingData }) => {
      const response = await api.put(`/buildings/${id}`, data);
      return response.data as Building;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
      queryClient.invalidateQueries({ queryKey: queryKeys.building(id) });
    },
  });
};

export const useDeleteBuilding = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/buildings/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
    },
  });
};

// Floor hooks
export const useFloors = (buildingId: string) => {
  return useQuery({
    queryKey: queryKeys.floors(buildingId),
    queryFn: async () => {
      // api.get() returns JSON directly: { success: true, data: { floors: [...], pagination: {...} } }
      const response = await api.get(`/floors?buildingId=${buildingId}`);
      if (response.success && response.data && response.data.floors) {
        return response.data.floors as Floor[];
      }
      throw new Error(response.error?.message || 'Failed to fetch floors');
    },
    enabled: !!buildingId,
  });
};

export const useFloor = (id: string) => {
  return useQuery({
    queryKey: queryKeys.floor(id),
    queryFn: async () => {
      const response = await api.get(`/floors/${id}`);
      return response.data as Floor;
    },
    enabled: !!id,
  });
};

export const useCreateFloor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateFloorData) => {
      const response = await api.post('/floors', data);
      return response.data as Floor;
    },
    onSuccess: (_, { buildingId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.floors(buildingId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
    },
  });
};

export const useUpdateFloor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateFloorData }) => {
      const response = await api.put(`/floors/${id}`, data);
      return response.data as Floor;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.floors(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
    },
  });
};

export const useDeleteFloor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/floors/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allFloors });
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
    },
  });
};

// Room hooks
export const useRooms = (floorId?: string) => {
  return useQuery({
    queryKey: queryKeys.rooms(floorId || 'all'),
    queryFn: async () => {
      const url = floorId ? `/rooms?floorId=${floorId}` : '/rooms';
      // api.get() returns JSON: { success: true, data: { data: [...rooms...], pagination: {...} } }
      const response = await api.get(url);
      if (response.success && response.data && response.data.data) {
        return response.data.data as Room[];
      }
      throw new Error(response.error?.message || 'Failed to fetch rooms');
    },
    enabled: true, // Always enabled, floorId is optional
    retry: 3,
    retryDelay: 1000,
  });
};

export const useRoom = (id: string) => {
  return useQuery({
    queryKey: queryKeys.room(id),
    queryFn: async () => {
      const response = await api.get(`/rooms/${id}`);
      if (response.success && response.data) {
        // Individual room endpoint returns room directly in data field
        return response.data as Room;
      }
      throw new Error(response.error?.message || 'Failed to fetch room');
    },
    enabled: !!id,
    retry: 3,
    retryDelay: 1000,
  });
};

export const useCreateRoom = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateRoomData) => {
      // Extract template IDs before creating room
      const { sessionTemplateIds, ...roomData } = data;
      
      // Create room first
      const response = await api.post('/rooms', roomData);
      const createdRoom = response.data as Room;
      
      // Then assign session templates if provided
      if (sessionTemplateIds && sessionTemplateIds.length > 0) {
        await api.post(`/rooms/${createdRoom.id}/session-templates`, {
          sessionTemplateIds,
          markDefaultId: sessionTemplateIds[0] // First template as default
        });
      }
      
      return createdRoom;
    },
    onSuccess: (_, { floorId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms(floorId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.floors(floorId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
      queryClient.invalidateQueries({ queryKey: ['session-templates'] }); // Invalidate template stats
    },
  });
};

export const useUpdateRoom = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRoomData }) => {
      const response = await api.put(`/rooms/${id}`, data);
      return response.data as Room;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.allFloors });
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
      
      // Invalidate planner queries to refresh grid with new session templates
      queryClient.invalidateQueries({ queryKey: ['planner-grid'] });
      queryClient.invalidateQueries({ queryKey: ['planner-filters'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan-data'] });
    },
  });
};

export const useDeleteRoom = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/rooms/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.allRooms });
      queryClient.invalidateQueries({ queryKey: queryKeys.allFloors });
      queryClient.invalidateQueries({ queryKey: queryKeys.buildings });
    },
  });
};